import React from 'react';
import { formatAdminUtcDate } from '../../lib/adminDates';
import type { AdminDemoLeadListItem } from '../../legalTypes';
import { AdminDataTable, StatusPill, type AdminDataTableColumn } from '../components';

const statusTone = (status: string) => {
  if (status === 'converted') {
    return 'success' as const;
  }
  if (status === 'reviewing') {
    return 'info' as const;
  }
  if (status === 'rejected') {
    return 'critical' as const;
  }
  return 'neutral' as const;
};

const columns: AdminDataTableColumn<AdminDemoLeadListItem>[] = [
  {
    id: 'company',
    header: 'Company',
    accessor: (lead) => lead.companyName,
    render: (lead) => <strong>{lead.companyName}</strong>,
    sortable: true,
    width: 'minmax(190px, 1.4fr)',
  },
  {
    id: 'contact',
    header: 'Contact',
    accessor: (lead) => `${lead.firstName} ${lead.lastName}`,
    render: (lead) => (
      <span className="admin-legal-contact">
        <span>{lead.firstName} {lead.lastName}</span>
        <span className="admin-legal-contact__secondary" data-visual-mask>{lead.email}</span>
      </span>
    ),
    sortable: true,
    width: 'minmax(190px, 1fr)',
  },
  {
    id: 'kind',
    header: 'Kind',
    accessor: (lead) => lead.kind,
    width: '110px',
  },
  {
    id: 'status',
    header: 'Review state',
    accessor: (lead) => lead.status,
    render: (lead) => (
      <StatusPill tone={statusTone(lead.status)}>
        <span aria-hidden="true">[{lead.status === 'converted' ? '✓' : '•'}]</span>{' '}
        {lead.status.replace(/_/g, ' ')}
      </StatusPill>
    ),
    sortable: true,
    width: '150px',
  },
  {
    id: 'created',
    header: 'Received',
    accessor: (lead) => lead.createdAtUtc,
    render: (lead) => <time data-visual-mask dateTime={lead.createdAtUtc}>{formatAdminUtcDate(lead.createdAtUtc)}</time>,
    sortable: true,
    width: '120px',
  },
];

interface DemoLeadTableProps {
  leads: AdminDemoLeadListItem[];
  onOpen: (lead: AdminDemoLeadListItem) => void;
  emptyState: React.ReactNode;
}

const DemoLeadTable: React.FC<DemoLeadTableProps> = ({ leads, onOpen, emptyState }) => (
  <AdminDataTable
    ariaLabel="Demo leads"
    rows={leads}
    columns={columns}
    getRowId={(lead) => lead.id}
    getRowLabel={(lead) => `${lead.companyName} demo lead`}
    onRowActivate={onOpen}
    emptyState={emptyState}
  />
);

export default DemoLeadTable;
